import styled from "styled-components";
import { Tree, TreeNode } from "react-organizational-chart";
import {
  directLeftGroups,
  managementOffice,
  mainOrganizations,
} from "./organizationData";

const AoneOrganization = () => {
  return (
    <Container>
      <Tree
        lineWidth="2px"
        lineColor="#DDE3EC"
        lineBorderRadius="6px"
        nodePadding="3px"
        label={
          <TopArea>
            <RepBox>대표</RepBox>
            <DirectSubordinatesSection>
              <LeftBranchAxis />
              {directLeftGroups.map((group, gIdx) => (
                <SubordinateRow key={gIdx}>
                  <LeftPart>
                    {group.length === 1 ? (
                      <DirectBox>{group[0].title}</DirectBox>
                    ) : (
                      <StepRow>
                        {group.map((item, iIdx) => (
                          <DirectBox key={iIdx}>{item.title}</DirectBox>
                        ))}
                      </StepRow>
                    )}
                  </LeftPart>
                  <RightPart>
                    {gIdx === 1 && (
                      <>
                        <DirectBox>{managementOffice.title}</DirectBox>
                        <ManagementTeams>
                          {managementOffice.teams.map((team, idx) => (
                            <TeamBox key={idx}>{team}</TeamBox>
                          ))}
                        </ManagementTeams>
                      </>
                    )}
                  </RightPart>
                </SubordinateRow>
              ))}
            </DirectSubordinatesSection>
          </TopArea>
        }
      >
        {mainOrganizations.map((mainOrg, idx) => (
          <TreeNode
            key={idx}
            label={<MainOrgBox>{mainOrg.title}</MainOrgBox>}
          >
            {mainOrg.children.map((child, cIdx) => (
              <TreeNode
                key={cIdx}
                label={
                  <SubDepartment>
                    <DepartmentBox>{child.title}</DepartmentBox>
                    <TeamList>
                      {child.teams.map((team, tIdx) => (
                        <TeamBox key={tIdx}>{team}</TeamBox>
                      ))}
                      {child.extraTeams?.map((team, eIdx) => (
                        <TeamBox
                          key={`e${eIdx}`}
                          $gapTop={eIdx === 0}
                        >
                          {team}
                        </TeamBox>
                      ))}
                    </TeamList>
                  </SubDepartment>
                }
              />
            ))}
          </TreeNode>
        ))}
      </Tree>
    </Container>
  );
};
export default AoneOrganization;

const Container = styled.div`
  width: 100%;
  overflow-x: auto;
  padding: 1.5rem .25rem;
`;

const Box = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: .3rem .6rem;
  min-width: 3.5rem;
  box-shadow: ${({ theme }) => theme.shadow.sm};
  border-radius: .6rem;
  font-size: .75rem;
  white-space: nowrap;
  transition: font-weight .15s;
  &:hover {
    font-weight: bolder;
  }
`;

const RepBox = styled(Box)`
  background-color: ${({ theme }) => theme.colors.dark};
  color: ${({ theme }) => theme.colors.foreground.inverted};
  font-size: 1rem;
  padding: .5rem 1.2rem;
`;

const DirectBox = styled(Box)`
  background-color: ${({ theme }) => theme.colors.medium};
  color: ${({ theme }) => theme.colors.foreground.inverted};
`;

const MainOrgBox = styled(Box)`
  background-color: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.foreground.inverted};
`;

const DepartmentBox = styled(Box)`
  background-color: ${({ theme }) => theme.colors.medium};
  color: ${({ theme }) => theme.colors.foreground.inverted};
`;

const TeamBox = styled(Box)`
  background-color: ${({ theme }) => theme.colors.background.extralight};
  ${({ $gapTop }) => $gapTop && "margin-top: 1rem;"}
`;

const TopArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
`;

const DirectSubordinatesSection = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 40rem;
  padding-top: 1.2rem;
  padding-bottom: .5rem;

  /* Continuous central vertical spine (from RepBox bottom down to where Tree's auto-line begins) */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    background: ${({ theme }) => theme.colors.monotone.light};
    transform: translateX(-50%);
  }

  @media (max-width: 991px) {
    width: auto;
    padding-top: .5rem;
    padding-bottom: 0;
    &::before { display: none; }
  }
`;

const SubordinateRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  width: 100%;
  margin: .3rem 0;

  @media (max-width: 991px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: .25rem;
  }
`;

const LeftPart = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding-right: 3.6rem;
  position: relative;

  /* Short horizontal connector from box's right edge to sub-spine (LeftBranchAxis) */
  &:not(:empty)::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 3rem;
    width: .6rem;
    height: 2px;
    background: ${({ theme }) => theme.colors.monotone.light};
    transform: translateY(-50%);
  }

  @media (max-width: 991px) {
    justify-content: center;
    padding-right: 0;
    &::after { display: none; }
  }
`;

const RightPart = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  padding-left: .6rem;
  position: relative;

  /* Horizontal connector from spine to first box's left edge */
  &:not(:empty)::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: .6rem;
    height: 2px;
    background: ${({ theme }) => theme.colors.monotone.light};
    transform: translateY(-50%);
  }

  @media (max-width: 991px) {
    justify-content: center;
    padding-left: 0;
    &::before { display: none; }
  }
`;

/* Sub-spine for the left organization group (전문위원실 / 현대관세무역연구원 / 품목분류원산지연구소).
   Row 1's grid cell is taller (~5.9rem) because RightPart contains 경영기획실 + 경영기획팀/재경팀/DX팀,
   so row centers are (measured): row 0 ~2.4rem, row 1 ~6.85rem, row 2 ~11.3rem.
   This axis spans row 0 center → row 2 center (height 8.9rem); its midpoint coincides with row 1 center,
   so the long horizontal connector lands exactly on the 현대관세무역연구원 / 경영기획실 line.
   경영기획실 팀 수가 바뀌면 top/height를 다시 맞춰야 한다. */
const LeftBranchAxis = styled.div`
  position: absolute;
  left: 17rem;
  top: 2.4rem;
  height: 8.9rem;
  width: 2px;
  background: ${({ theme }) => theme.colors.monotone.light};

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 3rem;
    height: 2px;
    background: ${({ theme }) => theme.colors.monotone.light};
    transform: translateY(-50%);
  }

  @media (max-width: 991px) {
    display: none;
  }
`;

const StepRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: .5rem;

  /* Horizontal connector between the two boxes (식품검역검사연구소 — 품목분류원산지연구소) */
  & > *:first-child {
    position: relative;
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 100%;
      width: .5rem;
      height: 2px;
      background: ${({ theme }) => theme.colors.monotone.light};
      transform: translateY(-50%);
    }
  }
`;

const ManagementTeams = styled.div`
  display: flex;
  flex-direction: column;
  gap: .25rem;
  margin-left: 1.2rem;
  position: relative;

  /* Local spine for 경영기획팀/재경팀 */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: -.6rem;
    width: 2px;
    height: calc(100% - 1.5rem);
    background: ${({ theme }) => theme.colors.monotone.light};
  }

  /* Horizontal connector from 경영기획실 right edge to local spine */
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: -1.2rem;
    width: .6rem;
    height: 2px;
    background: ${({ theme }) => theme.colors.monotone.light};
    transform: translateY(-50%);
  }

  /* Horizontal taps from local spine to each team box */
  & > * {
    position: relative;
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      right: 100%;
      width: .6rem;
      height: 2px;
      background: ${({ theme }) => theme.colors.monotone.light};
      transform: translateY(-50%);
    }
  }

  @media (max-width: 991px) {
    margin-left: 0;
    align-items: center;
    &::before, &::after { display: none; }
    & > *::before { display: none; }
  }
`;

const SubDepartment = styled.div`
  display: flex;
  flex-direction: column;
  gap: .35rem;
  align-items: stretch;
  min-width: 5rem;
`;

const TeamList = styled.div`
  display: flex;
  flex-direction: column;
  gap: .35rem;
  & > * {
    // width: 100%;
  }
`;
